import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import WidgetsRoundedIcon from "@mui/icons-material/WidgetsRounded";
import { selectUser } from "../../features/userSlice";
import { selectRoles } from "../../features/roleSlice";
import { checkRoles } from "../Navigation/SideNav";
import { SectionHeader } from "../UI/Page";
import { widgetCatalog, widgetComponentFor } from "./widgets/registry";
import GenericPageWidget from "./widgets/GenericPageWidget";

// Page Access grants (utils/routeAccess.js) count as well as roles.
const canSee = (item, mine, granted) =>
  checkRoles(item.roles, mine, item.path, granted);

// Chosen widgets are saved per user in this browser's localStorage, same
// approach as homeFavorites.
const storageKey = (user) => `homeWidgets:${user?.id || "anon"}`;

const HomeWidgets = () => {
  const user = useSelector(selectUser);
  const role = useSelector(selectRoles);
  const userRoles = role?.roles || [];
  const grantedPaths = role?.grantedPaths;
  const [chosen, setChosen] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    try {
      const raw = localStorage.getItem(storageKey(user));
      const parsed = raw ? JSON.parse(raw) : [];
      setChosen(Array.isArray(parsed) ? parsed : []);
    } catch {
      setChosen([]);
    }
  }, [user]);

  const save = (next) => {
    setChosen(next);
    try {
      localStorage.setItem(storageKey(user), JSON.stringify(next));
    } catch {
      // Storage full/blocked — widgets just won't persist.
    }
  };

  const toggle = (path) =>
    save(
      chosen.includes(path)
        ? chosen.filter((p) => p !== path)
        : [...chosen, path]
    );

  const visibleSections = useMemo(
    () =>
      widgetCatalog
        .map((section) => ({
          ...section,
          items: section.items.filter((item) =>
            canSee(item, userRoles, grantedPaths)
          ),
        }))
        .filter((section) => section.items.length > 0),
    [userRoles, grantedPaths]
  );

  const byPath = useMemo(() => {
    const map = new Map();
    widgetCatalog.forEach((section) =>
      section.items.forEach((item) => map.set(item.path, item))
    );
    return map;
  }, []);

  // Role changes and removed pages drop out silently instead of rendering
  // dead tiles.
  const active = chosen
    .map((path) => byPath.get(path))
    .filter((page) => page && canSee(page, userRoles, grantedPaths));

  return (
    <section aria-labelledby="home-widgets-title">
      <SectionHeader
        title={<span id="home-widgets-title">My widgets</span>}
        description="Live, at-a-glance summaries of the pages you use."
        actions={
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="app-btn app-btn-ghost app-btn-sm"
          >
            <TuneRoundedIcon sx={{ fontSize: 17 }} />
            Edit
          </button>
        }
      />

      {active.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {active.map((page) => {
            const Live = widgetComponentFor(page.path);
            return Live ? (
              <Live key={page.path} />
            ) : (
              <GenericPageWidget key={page.path} page={page} />
            );
          })}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="group flex w-full flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-[color:var(--app-input-border)] px-6 py-10 text-center transition hover:border-sky-400 hover:bg-[color:var(--app-hover)]"
        >
          <span className="app-icon-tile bg-violet-500/15 text-violet-600 transition group-hover:scale-105 dark:text-violet-300">
            <WidgetsRoundedIcon />
          </span>
          <span className="app-muted max-w-md text-sm leading-relaxed group-hover:text-[color:var(--app-text)]">
            Add widgets — live, tile-sized summaries of the pages you use — and
            they'll show up here.
          </span>
        </button>
      )}

      <Dialog
        fullWidth
        maxWidth="sm"
        open={open}
        onClose={() => setOpen(false)}
        scroll="paper"
        aria-labelledby="widget-editor-title"
      >
        <DialogTitle
          id="widget-editor-title"
          className="flex items-center justify-between gap-3"
          sx={{ pb: 1 }}
        >
          Choose widgets
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close widget editor"
            className="app-icon-btn -mr-2 h-9 w-9"
          >
            <CloseRoundedIcon fontSize="small" />
          </button>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <p className="app-muted mb-5 text-sm leading-relaxed">
            Pages marked <span className="font-semibold">Live</span> show a
            data summary; the rest add a shortcut tile. Widgets are saved on
            this browser.
          </p>
          <div className="space-y-5">
            {visibleSections.map((section) => (
              <div key={section.label}>
                <p className="app-eyebrow mb-1.5 px-1">{section.label}</p>
                <div className="app-surface divide-y divide-[color:var(--app-divider)] overflow-hidden rounded-2xl border">
                  {section.items.map((item) => {
                    const added = chosen.includes(item.path);
                    return (
                      <button
                        key={item.path}
                        type="button"
                        onClick={() => toggle(item.path)}
                        aria-pressed={added}
                        className="flex min-h-[2.75rem] w-full items-center justify-between gap-3 px-3.5 py-2 text-left text-sm transition-colors hover:bg-[color:var(--app-hover)]"
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          <span
                            className={`truncate ${added ? "font-semibold" : ""}`}
                          >
                            {item.name}
                          </span>
                          {item.live && (
                            <span className="shrink-0 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                              Live
                            </span>
                          )}
                        </span>
                        {added ? (
                          <CheckCircleRoundedIcon
                            sx={{ fontSize: 22 }}
                            className="shrink-0 text-sky-600 dark:text-sky-300"
                          />
                        ) : (
                          <AddCircleOutlineRoundedIcon
                            sx={{ fontSize: 22 }}
                            className="app-muted shrink-0"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <span className="app-muted mr-auto text-xs">
            {active.length} added
          </span>
          <Button variant="contained" onClick={() => setOpen(false)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </section>
  );
};

export default HomeWidgets;
